import type { TbHttp } from "./http";
import type { EntityRef } from "./types";

declare const relBrand: unique symbol;
type RelBrand<B> = { readonly [relBrand]: B };

type RelationBase = {
  from: { id: string; entityType: string };
  to: { id: string; entityType: string };
  typeGroup?: string;
  additionalInfo?: Record<string, unknown> | null;
};

export type ContainsRelation = RelationBase & { type: "Contains" } & RelBrand<"Contains">;
export type AddRelation      = RelationBase & { type: "Add"      } & RelBrand<"Add">;
export type SubsRelation     = RelationBase & { type: "Subs"     } & RelBrand<"Subs">;

export type RelationInfo = RelationBase & {
  type: string;
  toName?: string;
  toEntityTypeName?: string;
  fromName?: string;
  fromEntityTypeName?: string;
};

export type ContainsChild = {
  relation: ContainsRelation;
  child: EntityRef;
  childName?: string;
};

export type ContainsParent = {
  relation: ContainsRelation;
  parent: EntityRef;
  parentName?: string;
};

export function listRelationsFrom(http: TbHttp, parent: EntityRef): Promise<RelationInfo[]> {
  const p = new URLSearchParams({ fromId: parent.id, fromType: parent.type });
  return http.get(`/api/relations/info?${p}`);
}

export function listRelationsTo(http: TbHttp, child: EntityRef): Promise<RelationInfo[]> {
  const p = new URLSearchParams({ toId: child.id, toType: child.type });
  return http.get(`/api/relations/info?${p}`);
}

/** Direct `Contains` children. DO NOT mix with Add/Subs. */
export async function listContainsChildren(http: TbHttp, parent: EntityRef): Promise<ContainsChild[]> {
  const all = await listRelationsFrom(http, parent);
  return all
    .filter((r) => r.type === "Contains")
    .map((r) => ({
      relation: r as unknown as ContainsRelation,
      child: { type: r.to.entityType as EntityRef["type"], id: r.to.id },
      childName: r.toName,
    }));
}

export async function listContainsParents(http: TbHttp, child: EntityRef): Promise<ContainsParent[]> {
  const all = await listRelationsTo(http, child);
  return all
    .filter((r) => r.type === "Contains")
    .map((r) => ({
      relation: r as unknown as ContainsRelation,
      parent: { type: r.from.entityType as EntityRef["type"], id: r.from.id },
      parentName: r.fromName,
    }));
}
